import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { isApiError } from '../lib/api/errors';
import { isServiceUnavailableActive } from '../lib/api/client';
import { showErrorModal } from '../services/errorModalService';

/**
 * The global error modal is the DEFAULT for every failed query and mutation
 * (FE plan §3.3) — the same modal as the consumer app: translated message,
 * correlation id, and "Contact support" into Ybug. A query or mutation that
 * shows its own error inline opts out with `meta: { silent: true }`.
 *
 * Never shown for:
 *   - 401 — the client already ran refresh → logout; there is nothing to say.
 *   - 503 — the full-screen service-unavailable page is up.
 *   - non-API errors (network TypeError) — the no-network page handles it;
 *     programming errors surface through the error boundary instead.
 */
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { silent?: boolean };
    mutationMeta: { silent?: boolean };
  }
}

/** Fired when the BE refuses an /admin call with MFA_REQUIRED; the MFA gate re-checks and blocks. */
export const MFA_REQUIRED_EVENT = 'admin:mfa-required';

export function reportApiError(error: unknown, silent?: boolean): void {
  if (!isApiError(error)) return;
  // Not an error for the user to read: the MFA gate takes over with its modals.
  if (error.code === 'MFA_REQUIRED') {
    window.dispatchEvent(new Event(MFA_REQUIRED_EVENT));
    return;
  }
  if (silent) return;
  if (error.status === 401 || error.status === 503 || isServiceUnavailableActive()) return;
  showErrorModal(error.userMessage, error.correlationId, error.code);
}

/**
 * 4xx and 501 are answers, not blips — retrying a 403 or a 404 three times
 * just keeps a spinner up for the same result. 5xx and network errors keep
 * TanStack's default retries.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && ((error.status >= 400 && error.status < 500) || error.status === 501)) return false;
  return failureCount < 3;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => reportApiError(error, query.meta?.silent),
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => reportApiError(error, mutation.meta?.silent),
    }),
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        staleTime: 30_000,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export const queryClient = createQueryClient();
